import { FirstEnergyExportHelpers } from '../elements/katapult-maps-desktop/exports/excel_export_functions/export_helpers/FirstEnergyExportHelpers.js';
import { DataViews } from './DataViews.js';
import { ModelDefaults } from './ModelDefaults.js';
import { PickAnAttribute } from './PickAnAttribute.js';

/**
 * Retrieves a list of nodes with their corresponding data.
 * @param {Object} jobData - The job data object.
 * @param {string} userGroup - The user group.
 * @returns {Promise<Array<Object>>} - A promise that resolves to an array of node objects.
 */
export async function getSpansNodeList(jobData, userGroup, options) {
  options = options || {};
  let nodeList = [];
  let nodes = jobData?.nodes;
  if (!nodes) return nodeList;
  let nodeKeys = Object.keys(nodes);
  let headers = FirstEnergyExportHelpers.firstEnergyProfileHeaderSet();
  let modelDefaults = ModelDefaults();
  let jobCreator = jobData?.job_creator;
  let emailCache = {};
  if (!jobCreator) return nodeList;
  let otherAttributes = await FirebaseWorker.ref(`photoheight/company_space/${jobCreator}/models/attributes`)
    .once('value')
    .then((s) => s.val());
  let users = FirebaseWorker.ref(`photoheight/company_space/${userGroup}/users`)
    .once('value')
    .then((s) => s.val());
  let context = {
    nodes: nodes,
    connections: jobData?.connections,
    useMetricUnits: jobData?.useMetricUnits,
    traces: jobData?.traces?.trace_data,
    traceItems: jobData?.traces?.trace_items,
    otherAttributes: otherAttributes,
    photos: jobData?.photos,
    jobName: jobData?.name,
    modelDefaults: modelDefaults,
    userGroup: userGroup,
    jobCreator: jobData?.job_creator,
    user: users
  };
  let { exportDataSet } = FirstEnergyExportHelpers.getFirstEnergyProfileData(
    { includeProposedWithComs: true, webAutomation: true, attachmentsPerPage: options?.attachmentsPerPage },
    headers,
    null,
    context
  );
  for (let key of nodeKeys) {
    //find the key in the exportDataSet
    let nodeData = exportDataSet.find((node) => node.nodeId === key);
    if (nodeData) {
      let time_buckets = nodes[key]?.attributes?.time_bucket;
      let uid = '';
      if (time_buckets) {
        let timeBucketDate = 0;
        for (let key in time_buckets) {
          if (time_buckets[key].stop > timeBucketDate) {
            timeBucketDate = time_buckets[key].stop;
            uid = time_buckets[key].uid;
          }
        }
      }
      let email = emailCache[uid] ?? (emailCache[uid] = await getCollectorsEmail(uid));
      let nodeExportObject = nodeData?.exportObject;
      let comms = {};
      for (let i = 1; i <= 6; i++) {
        if (!nodeExportObject[`com${i} Name`]) break;
        const marker = nodeExportObject[`com${i} Marker`];
        let comm = {};
        comm[`name`] = nodeExportObject[`com${i} Name`];
        comm[`proposed`] = jobData?.traces?.trace_data?.[nodeExportObject[`com${i} Marker`]?._trace]?.proposed ?? false;
        comm[`front_or_back`] = nodeExportObject[`com${i} Front/Back`];
        comm[`height`] = nodeExportObject[`com${i} HT`];
        const proposedHeight = DataViews.help.getProposedMarkerHeight(marker, false, false);
        comm[`proposed_height`] = proposedHeight;
        comm[`midspan_height`] = nodeExportObject[`com${i} Midspan Height`];
        comm[`midspan_height_back`] = nodeExportObject[`com${i} Midspan Height Back`];
        comms[`com${i}`] = comm;
      }
      let lowestPowerAttachment = '';
      if (nodeExportObject['Low Power Equipment']) lowestPowerAttachment = nodeExportObject['Low Power Equipment'];
      else {
        if (nodeExportObject['Low Power Neutral'] === 'X') lowestPowerAttachment = 'Neutral';
        else if (nodeExportObject['Low Power Secondary'] === 'X') lowestPowerAttachment = 'Secondary';
        else if (nodeExportObject['Low Power Primary'] === 'X') lowestPowerAttachment = 'Primary';
        else if (nodeExportObject['Low Power Other'] === 'X') lowestPowerAttachment = 'Other';
      }
      let node = {
        node_id: key,
        power_company_pole_no: nodeExportObject['Power Tag'],
        telco_pole_no: nodeExportObject['Telco Tag'],
        attacher: nodeExportObject['Attacher Name'],
        street: nodeExportObject['Street'],
        municipality: nodeExportObject['Municipality'],
        date: nodeExportObject['Date'],
        collectors_email: email.email,
        attachment_type: nodeExportObject['Proposed Cable Type'],
        pole_class: PickAnAttribute(nodes[key]?.attributes, 'pole_class'),
        pole_height: PickAnAttribute(nodes[key]?.attributes, 'pole_height'),
        transformer_on_pole: nodeExportObject['Device Yes'] == 'X' ? true : nodeExportObject['Device No'] == 'X' ? false : null,
        street_light_on_pole: nodeExportObject['STLT Yes'] == 'X' ? true : nodeExportObject['STLT No'] == 'X' ? false : null,
        street_light_height: nodeExportObject['STLT Bracket'],
        street_light_drip_loop: nodeExportObject['STLT Drip Loop'],
        lowest_drip_loop: nodeExportObject['Lowest Drip Loop'],
        guy_required: nodeExportObject['Guying Required Yes'] == 'X' ? true : nodeExportObject['Guying Required No'] == 'X' ? false : null,
        conduit_riser: nodeExportObject['Riser Yes'] == 'X' ? true : nodeExportObject['Riser No'] == 'X' ? false : null,
        first_down_guy: nodeExportObject['Down Guy Anchor 1'] == 'X' ? true : false,
        second_down_guy: nodeExportObject['Down Guy Anchor 2'] == 'X' ? true : false,
        pole_to_pole: nodeExportObject['Pole to Pole'] == 'X' ? true : false,
        pole_to_pole_number: nodeExportObject['Pole to Pole #'],
        sidewalk_guy: nodeExportObject['Sidewalk Guy'] == 'X' ? true : false,
        brace_pole: nodeExportObject['Brace Pole'] == 'X' ? true : false,
        slack_span: nodeExportObject['slsp'] == 'X' ? true : false,
        before_pole_number: nodeExportObject['Pole # Before'],
        after_pole_number: nodeExportObject['Pole # After'],
        before_span_length: nodeExportObject['Midspan Length Back'],
        after_span_length: nodeExportObject['Midspan Length'],
        riser_height: nodeExportObject['Riser Height'],
        lowest_power_attachment: lowestPowerAttachment,
        lowest_power_before_height: nodeExportObject['Low Power Midspan Height Back'],
        lowest_power_side: nodeExportObject['Low Power Front/Back'],
        lowest_power_attachment_height: nodeExportObject['Low Power Ht'],
        low_power_equipment_height: nodeExportObject['Low Power Equipment Height'],
        low_power_midspan_after_height: nodeExportObject['Low Power Midspan Height'],
        comm_attachments: comms,
        notes: nodeExportObject['Notes'],
        make_ready_comments: nodeExportObject['Make Ready Comments'],
        crosses_over: nodeExportObject['MSF X Types'] || nodeExportObject['MSB X Types']
      };
      nodeList.push(node);
    }
  }
  return nodeList;
}

/**
 * Gets the email of the collector who has the latest time bucket.
 *
 * @param String uid The node object.
 * @returns {Promise<>} The email of the collector who has the latest time bucket, or an empty string if no time bucket or email is found.
 */
async function getCollectorsEmail(uid) {
  // only include if we have a time_bucket
  let email = '';
  if (uid) {
    email = await FirebaseWorker.ref(`user_info/${uid}/email`)
      .once('value')
      .then((s) => s.val());
  }
  return { uid: uid, email: email };
}
